// One-click record-latency calibration via acoustic loopback: play sharp clicks
// out the speakers, record them back through the mic, and measure the delay
// between scheduled and captured = real round-trip latency.

export interface CalibrationResult { roundTripMs: number; samples: number; inputLabel?: string; }
export interface CalibrationOptions {
  /** Specific microphone to listen on (else the system default). */
  inputDeviceId?: string | null;
  /** Specific speakers to play the clicks from (else the current output). */
  outputDeviceId?: string | null;
}

export async function calibrateLatency(
  ctx: AudioContext,
  opts: CalibrationOptions = {},
): Promise<CalibrationResult | { error: string }> {
  let stream: MediaStream;
  try {
    const audio: MediaTrackConstraints = { echoCancellation: false, noiseSuppression: false, autoGainControl: false };
    if (opts.inputDeviceId) audio.deviceId = { exact: opts.inputDeviceId };
    stream = await navigator.mediaDevices.getUserMedia({ audio });
  } catch {
    return { error: "Microphone unavailable — allow mic access (or pick a different mic) to calibrate." };
  }
  const inputLabel = stream.getAudioTracks()[0]?.label || "default microphone";

  // Route the clicks to the chosen speakers for the duration; restore after.
  const anyCtx = ctx as any;
  let prevSink: any;
  let sinkChanged = false;
  if (opts.outputDeviceId && typeof anyCtx.setSinkId === "function") {
    prevSink = anyCtx.sinkId;
    try { await anyCtx.setSinkId(opts.outputDeviceId); sinkChanged = true; } catch { /* keep current output */ }
  }

  const sr = ctx.sampleRate;
  const src = ctx.createMediaStreamSource(stream);
  const node = new AudioWorkletNode(ctx, "recorder-processor");
  const sink = ctx.createGain(); sink.gain.value = 0;
  src.connect(node); node.connect(sink); sink.connect(ctx.destination);

  let startTime = 0;
  const blocks: Float32Array[] = [];
  let resolveStarted: () => void;
  const started = new Promise<void>((r) => (resolveStarted = r));
  node.port.onmessage = (e) => {
    const d = e.data;
    if (d.type === "started") { startTime = d.time; resolveStarted(); }
    else if (d.type === "data") { blocks.push(d.chans[0]); }
  };

  const cleanup = () => {
    try { node.port.postMessage("stop"); } catch {}
    try { src.disconnect(); node.disconnect(); sink.disconnect(); } catch {}
    stream.getTracks().forEach((t) => t.stop());
    if (sinkChanged) { try { anyCtx.setSinkId(typeof prevSink === "string" ? prevSink : ""); } catch {} }
  };

  const clickTimes: number[] = [];
  try {
    const postStart = ctx.currentTime;
    node.port.postMessage("start");
    // Wait briefly for the worklet's capture-start timestamp; fall back if needed
    // so the clicks ALWAYS play (and you always hear them).
    await Promise.race([started, new Promise((r) => setTimeout(r, 400))]);
    if (!startTime) startTime = postStart;

    // schedule 3 audible 1 kHz beeps out the speakers at known times
    const base = ctx.currentTime + 0.4;
    for (let k = 0; k < 3; k++) {
      const t = base + k * 0.35;
      clickTimes.push(t);
      const len = Math.floor(sr * 0.03); // 30 ms — clearly audible, sharp onset
      const b = ctx.createBuffer(1, len, sr);
      const d = b.getChannelData(0);
      for (let i = 0; i < len; i++) d[i] = Math.sin((2 * Math.PI * 1000 * i) / sr) * Math.exp(-i / (sr * 0.01));
      const bs = ctx.createBufferSource(); bs.buffer = b;
      const g = ctx.createGain(); g.gain.value = 0.85;
      bs.connect(g); g.connect(ctx.destination); bs.start(t);
    }

    // capture until ~0.8s after the last click
    await new Promise((r) => setTimeout(r, (base + 0.7 + 0.8 - ctx.currentTime) * 1000));
    node.port.postMessage("stop");
    await new Promise((r) => setTimeout(r, 200)); // flush trailing data
  } catch (e: any) {
    cleanup();
    return { error: "Calibration error: " + (e?.message || e) };
  }
  cleanup();

  // assemble mono recording
  const total = blocks.reduce((n, b) => n + b.length, 0);
  if (!total) return { error: `No audio was recorded from “${inputLabel}” — check the mic (or pick a different one) and try again.` };
  const data = new Float32Array(total);
  let off = 0; for (const b of blocks) { data.set(b, off); off += b.length; }

  const m = analyzeLoopback(data, startTime, clickTimes, sr);
  if (!m) return { error: `Couldn't hear the clicks on “${inputLabel}”. Use speakers (not headphones), unmute, raise volume — or pick a different mic/output — then retry.` };
  return { ...m, inputLabel };
}

/** Pure analysis: find each click's onset in the recording and return the median
 *  round-trip latency. Exported for testing. */
export function analyzeLoopback(data: Float32Array, startTime: number, clickTimes: number[], sr: number): CalibrationResult | null {
  // noise floor from the first 0.2 s (before the first click)
  let floor = 0;
  const n0 = Math.min(data.length, Math.floor(sr * 0.2));
  for (let i = 0; i < n0; i++) floor = Math.max(floor, Math.abs(data[i]));
  const thr = Math.max(floor * 6, 0.03);

  const rts: number[] = [];
  for (const tc of clickTimes) {
    const expIdx = Math.floor((tc - startTime) * sr);
    const from = Math.max(0, expIdx - Math.floor(sr * 0.02));
    const to = Math.min(data.length, expIdx + Math.floor(sr * 0.5));
    let onset = -1;
    for (let i = from; i < to; i++) { if (Math.abs(data[i]) > thr) { onset = i; break; } }
    if (onset >= 0) {
      const rt = (startTime + onset / sr) - tc;
      if (rt > 0 && rt < 0.6) rts.push(rt); // sane round-trip window
    }
  }
  if (!rts.length) return null;
  rts.sort((a, b) => a - b);
  const median = rts[Math.floor(rts.length / 2)];
  return { roundTripMs: Math.max(0, Math.round(median * 1000)), samples: rts.length };
}
